// SPEC §14 (S52b) — humanise le vocabulaire §7 / §12 pour l'affichage.
//
// exitReason est le libellé primaire (cause métier).
// closeSource est le libellé secondaire (canal de détection ; au survol / drawer).

export type ExitReasonValue =
  | "TRAIL"
  | "INITIAL_SL_HIT"
  | "BREAK_EVEN_STOP_HIT"
  | "BREAK_EVEN_PLUS_STOP_HIT"
  | "TRAILING_STOP_HIT"
  | "MANUAL_USER_CLOSE"
  | "MANUAL_AGE_LIMIT"
  | "MANUAL_BROKER_CLOSE"
  | "DEFENSIVE_HEDGE_CLOSED"
  | "TP_HIT"
  | "CIRCUIT_CYCLE_RISK_BUDGET"
  | "CIRCUIT_WORST_CASE_RISK"
  | "CIRCUIT_DAILY_LOSS_LIMIT"
  | "CIRCUIT_CYCLE_AGE_LIMIT"
  | "CIRCUIT_WEEKEND_HOLD"
  | "CIRCUIT_MANUAL_EMERGENCY_STOP"
  | "BROKER_CLOSED_UNKNOWN"
  | "ALL_POSITIONS_STOPPED_OUT"
  | "ALL_MANAGED_POSITIONS_CLOSED"
  // raisons de fin de cycle (page Historique)
  | "USER_STOP"
  | "MARKET_CLOSED";

export type CloseSourceValue =
  | "BOT_REQUEST"
  | "TRADE_SUBSCRIPTION"
  | "RECONCILE_POLL"
  | "RECONCILE_STARTUP"
  | "RECONCILE_RECONNECT";

const EXIT_REASON_LABELS: Record<ExitReasonValue, string> = {
  // Le moteur V6 écrit « TRAIL » pour ses clôtures trail décidées côté bot ;
  // TRAILING_STOP_HIT = le même événement détecté côté broker (IG). Même sens.
  TRAIL: "Trail touché",
  INITIAL_SL_HIT: "SL initial touché",
  BREAK_EVEN_STOP_HIT: "Stop break-even touché",
  BREAK_EVEN_PLUS_STOP_HIT: "Stop break-even+ touché",
  TRAILING_STOP_HIT: "Trail touché",
  MANUAL_USER_CLOSE: "Clôture utilisateur",
  MANUAL_AGE_LIMIT: "Clôture limite d'âge",
  MANUAL_BROKER_CLOSE: "Clôture broker manuelle",
  DEFENSIVE_HEDGE_CLOSED: "Hedge défensif fermé (hist.)",
  TP_HIT: "Take profit",
  CIRCUIT_CYCLE_RISK_BUDGET: "Breaker : budget risque du cycle",
  CIRCUIT_WORST_CASE_RISK: "Breaker : pire-cas §7",
  CIRCUIT_DAILY_LOSS_LIMIT: "Breaker : perte quotidienne",
  CIRCUIT_CYCLE_AGE_LIMIT: "Breaker : âge du cycle",
  CIRCUIT_WEEKEND_HOLD: "Breaker : week-end",
  CIRCUIT_MANUAL_EMERGENCY_STOP: "Breaker : arrêt d'urgence",
  BROKER_CLOSED_UNKNOWN: "Fermé par le broker (non classé)",
  ALL_POSITIONS_STOPPED_OUT: "Toutes les positions stoppées",
  ALL_MANAGED_POSITIONS_CLOSED: "Toutes les positions gérées fermées",
  USER_STOP: "Arrêté par l'utilisateur",
  MARKET_CLOSED: "Marché fermé (flatten)",
};

const CLOSE_SOURCE_LABELS: Record<CloseSourceValue, string> = {
  BOT_REQUEST: "Ordre de clôture du bot",
  TRADE_SUBSCRIPTION: "Subscription trade IG",
  RECONCILE_POLL: "Réconciliation en cycle",
  RECONCILE_STARTUP: "Réconciliation au démarrage",
  RECONCILE_RECONNECT: "Réconciliation post-reconnexion",
};

export function formatExitReason(raw: string | null | undefined): string {
  if (!raw) return "—";
  return EXIT_REASON_LABELS[raw as ExitReasonValue] ?? raw;
}

export function formatCloseSource(raw: string | null | undefined): string {
  if (!raw) return "—";
  return CLOSE_SOURCE_LABELS[raw as CloseSourceValue] ?? raw;
}

/**
 * Les raisons « échelle de stops » §7 alimentent l'agrégat « le ladder a-t-il fait son
 * travail ? ». BROKER_CLOSED_UNKNOWN est surfacé à part pour ne pas polluer les
 * comptes « stop touché ».
 */
export function isStopLadderReason(raw: string | null | undefined): boolean {
  return (
    raw === "INITIAL_SL_HIT" ||
    raw === "BREAK_EVEN_STOP_HIT" ||
    raw === "BREAK_EVEN_PLUS_STOP_HIT" ||
    raw === "TRAILING_STOP_HIT"
  );
}
